import { useEvent, usePropertyChanged, useStateRefInitial } from '@beesoft/common';
import {
  type ChangeEvent,
  forwardRef,
  memo,
  type Ref,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFieldContext } from '../../../../architecture/hooks/use-field-context/use-field.context';
import type {
  CheckboxCheckState,
  CheckboxElementProps,
  CheckboxProps,
  CheckboxRef,
  CheckboxRenderProps,
} from './checkbox.props';
import { HiddenField } from '../../../../architecture/components/hidden-field/hidden-field.component';
import { useRenderedMarkup } from '../../../../architecture/hooks/use-rendered-markup/use-rendered-markup.hook';
import { type Signal, useSignalEffect } from '@preact/signals-react';
import type { ComparatorFunction } from '../../../../architecture/hooks/use-data-comparator/use-data-comparator.props';
import { useHeadlessContext } from '../../../../architecture/hooks/use-headless-context';
import type { CheckboxChangeEvent } from '../checkboxes.interfaces';

const CheckboxComponent = (props: CheckboxProps, ref: Ref<CheckboxRef>) => {
  const { name, value, checked = false, partial = false, readOnly = false, onChange, children, className } = props;

  const [nameState, setNameState] = useState(name);
  const [readOnlyState, setReadOnlyState] = useState(readOnly);

  const nameSignal = useRef<Signal<string>>(undefined);
  const valueSignal = useRef<Signal<Array<unknown>>>(undefined);
  const readOnlySignal = useRef<Signal<boolean>>(undefined);
  const useComparator = useRef<Signal<boolean>>(undefined);
  const compare = useRef<ComparatorFunction>(undefined);
  const onCheckboxChange = useRef<(event?: CheckboxChangeEvent) => void>(undefined);

  const headlessContext = useHeadlessContext();
  const fieldContext = useFieldContext();
  const internalId = useId();

  const finalId = useMemo(
    () => fieldContext?.sharedId || props.id || internalId,
    [fieldContext?.sharedId, props.id, internalId],
  );

  const [checkedState, setCheckedState, checkedStateRef] = useStateRefInitial<CheckboxCheckState>({
    checked: false,
    partial: false,
  });

  const checkedProperty = usePropertyChanged(checked);
  const partialProperty = usePropertyChanged(partial);

  useEffect(() => {
    if (checkedState.initial) {
      setCheckedState({
        checked: partial ? false : checked,
        partial,
      });
    } else {
      const newChecked = !checkedProperty.changed ? checkedState.value.checked : checked;
      const newPartial = !partialProperty.changed ? checkedState.value.partial : partial;

      setCheckedState({
        checked: newPartial ? false : newChecked,
        partial: newPartial,
      });
    }
  }, [checked, partial]);

  useEffect(() => {
    setReadOnlyState(readOnly);
  }, [readOnly]);

  useEffect(() => {
    if (headlessContext) {
      nameSignal.current = headlessContext['nameSignal'] as Signal<string>;
      valueSignal.current = headlessContext['valueSignal'] as Signal<Array<unknown>>;
      readOnlySignal.current = headlessContext['readOnlySignal'] as Signal<boolean>;
      useComparator.current = headlessContext['useComparator'] as Signal<boolean>;
      compare.current = headlessContext['compare'] as ComparatorFunction;
      onCheckboxChange.current = headlessContext['onCheckboxChange'] as (event?: CheckboxChangeEvent) => void;
    }
  }, [headlessContext]);

  useSignalEffect(() => {
    if (valueSignal.current) {
      if (useComparator.current?.value && compare.current) {
        const compareFunc = compare.current;
        const checked = valueSignal.current.value?.some((item) => compareFunc(value, item)) ?? false;
        setCheckedState((prevState) => {
          return {
            ...prevState,
            checked,
          };
        });
      } else {
        const checked = valueSignal.current.value?.some((item) => value === item) ?? false;
        setCheckedState((prevState) => {
          return {
            ...prevState,
            checked,
          };
        });
      }
    }
  });

  useSignalEffect(() => {
    if (nameSignal.current) {
      setNameState(nameSignal.current.value);
    }
  });

  useSignalEffect(() => {
    if (readOnlySignal.current) {
      setReadOnlyState(readOnlySignal.current.value ?? false);
    }
  });

  const handleChangeEvent = useEvent((event: ChangeEvent<HTMLInputElement>) => {
    const checkedValue = checkedStateRef.current?.value.partial === true ? true : event.target.checked;
    setCheckedState({
      checked: checkedValue,
      partial: false,
    });

    if (!onCheckboxChange.current) {
      onChange?.({
        originalEvent: event,
        name: nameState,
        value,
        checked: checkedValue,
      });
    } else {
      onCheckboxChange.current({
        originalEvent: event,
        name: nameState,
        value,
        checked: checkedValue,
      });
    }
  });

  const setPartiallyChecked = (partiallyChecked: boolean) => {
    const state: CheckboxCheckState = {
      checked: partiallyChecked ? false : (checkedStateRef.current?.value.checked ?? false),
      partial: partiallyChecked,
    };

    setCheckedState(state);
  };

  const setChecked = (checked: boolean) => {
    setCheckedState({
      checked,
      partial: false,
    });
  };

  useImperativeHandle(ref, () => ({
    setPartiallyChecked,
    setChecked,
  }));

  const hiddenField = nameState && (
    <HiddenField
      id={finalId}
      name={nameState}
      type="checkbox"
      checked={checkedState.value.checked}
      readOnly={readOnlyState}
      onChange={handleChangeEvent}
    />
  );

  return useRenderedMarkup<CheckboxRenderProps, CheckboxElementProps>({
    wrapperElement: 'label',
    renderProps: {
      ...checkedState.value,
      readOnly: readOnlyState,
    },
    elementProps: {
      htmlFor: finalId,
    },
    className,
    innerWrapperElement: hiddenField,
    children,
  });
};

const Checkbox = memo(forwardRef(CheckboxComponent));
export { Checkbox };
